import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DateType, toDateOnlyString } from '../../../../core/date';
import { ReportParameterValues } from '../../report-parameters';
import { DatePeriodParameterOptions } from '../../report-parameters/parameters';
import { ReportDefinition, ReportResponseDto } from '../../reporting.model';
import { ReportKey } from '../report-key.enum';
import { deliveryReportColumnDefinition } from './delivery-report.column-definition';


export const DeliveryReport: ReportDefinition = {
    reportKey: ReportKey.Delivery,
    routePath: 'delivery',
    securityRuleKey: 'beta',
    reportOptions: {},
    defaultLayoutOptions: {
        pivotMode: false
    },
    reportColumnGroups: [
        {key: 'customerAddress', overrides: { header: 'Billing Address'}},
        {key: 'customerLocationAddress', overrides: { header: 'Delivery Address'}}
    ],
    reportColumnDefinition: deliveryReportColumnDefinition,
    parameters: [
        {
            key: 'datePeriod',
            overrides: {
                options: {
                    datePeriods: [
                        { key: 'currentMonth' },
                        { key: 'yearToDate' },
                        { key: 'fiscalYearToDate' },
                        { key: 'lastDeliveryByDivision' },
                        { key: 'last15DaysDeliveryByDivision' },
                        { key: 'last30DaysDeliveryByDivision' },
                        { key: 'custom' }
                    ]
                } as DatePeriodParameterOptions
            }
        },
        { key: 'transactionDateType' },
        { key: 'divisionHierarchyNodeKeys' },
        { key: 'productCategoryReportKeys' },
        { key: 'tradeClassKeys' },
        { key: 'deliveryTypeKeys' },
    ],
    fetchData: deliveryReportFetchData,
    integrationEndpoint: 'v1/reports/delivery',
    buildIntegrationQueryParams: buildDeliveryReportIntegrationQueryParams
};

function deliveryReportFetchData(httpClient: HttpClient, subscriberKey: number | null, params: ReportParameterValues): Observable<ReportResponseDto> {
    const dateType = params.transactionDateType === 'PostedDate' ? DateType.Posting : DateType.Transaction;

    const body = {
        startDate: toDateOnlyString(params.startDate),
        endDate: toDateOnlyString(params.endDate),
        dateType: dateType,
        divisionHierarchyNodeKeys: params.divisionHierarchyNodeKeys,
        productCategoryReportKeys: params.productCategoryReportKeys,
        tradeClassKeys: params.tradeClassKeys,
        deliveryTypeKeys: params.deliveryTypeKeys,
        useCompactColumns: true
    };

    return httpClient.post<ReportResponseDto>(`api/subscriber/${subscriberKey}/deliveries/reports/deliveries`, body);
}

function buildDeliveryReportIntegrationQueryParams(params: ReportParameterValues): Record<string, string | string[]> {
    const result: Record<string, string | string[]> = {};

    const datePeriod = params['datePeriod'] as string | null | undefined;
    if (datePeriod && datePeriod !== 'custom') {
        result['datePeriod'] = datePeriod;
    } else {
        const start = toDateOnlyString(params['startDate']);
        const end = toDateOnlyString(params['endDate']);
        if (start) result['startDate'] = start;
        if (end) result['endDate'] = end;
    }

    result['dateType'] = (params['transactionDateType'] as string) ?? 'TransactionDate';

    const divisionHierarchyNodeKeys = params['divisionHierarchyNodeKeys'] as number[] | undefined;
    if (divisionHierarchyNodeKeys?.length) result['divisionHierarchyNodeKeys'] = divisionHierarchyNodeKeys.map(String);

    const productCategoryReportKeys = params['productCategoryReportKeys'] as number[] | undefined;
    if (productCategoryReportKeys?.length) result['productCategoryReportKeys'] = productCategoryReportKeys.map(String);

    const tradeClassKeys = params['tradeClassKeys'] as number[] | undefined;
    if (tradeClassKeys?.length) result['tradeClassKeys'] = tradeClassKeys.map(String);

    const deliveryTypeKeys = params['deliveryTypeKeys'] as number[] | undefined;
    if (deliveryTypeKeys?.length) result['deliveryTypeKeys'] = deliveryTypeKeys.map(String);

    return result;
}
